import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Brain, Loader2, Sparkles } from "lucide-react";

export const KnowledgeSummaryBar = ({ userId, onEnriched }) => {
  const [status, setStatus] = useState(null);
  const running = status?.batch?.status === "running";

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/knowledge/status/${userId}`); setStatus(data); } catch (e) { console.error("knowledge status failed", e); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);
  useEffect(() => { if (status?.batch?.status === "done") onEnriched?.(); }, [status?.batch?.status, onEnriched]);

  const start = async () => {
    try {
      await axios.post(`${API}/knowledge/enrich-batch/${userId}`);
      toast.success("Researching your accounts in the background — customers first");
      load();
    } catch (e) { toast.error("Could not start research"); }
  };

  if (!status || (!status.customerCount && !status.wishlistCount)) return null;
  const unenriched = status.customerCount + status.wishlistCount - status.enrichedCount;

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="knowledge-summary-bar">
      <div className="flex items-center gap-3 text-sm text-gray-300 flex-wrap">
        <Brain className="h-4 w-4 text-ism-teal" />
        <span><span className="text-emerald-300">{status.customerCount}</span> customers</span>
        <span className="text-gray-600">·</span>
        <span><span className="text-ism-teal">{status.wishlistCount}</span> wishlist</span>
        <span className="text-gray-600">·</span>
        <span><span className="text-white">{status.enrichedCount}</span> researched</span>
        {status.annualSpendKnown > 0 && <><span className="text-gray-600">·</span><span>${Number(status.annualSpendKnown).toLocaleString()} known annual spend</span></>}
        {status.whitespace?.length > 0 && (
          <span className="text-xs text-gray-500" data-testid="knowledge-whitespace">Top gap: <span className="text-amber-300">{status.whitespace[0].product}</span> ({status.whitespace[0].count} accounts)</span>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={start} disabled={running || unenriched <= 0} className="border-ism-teal/30 text-ism-teal hover:bg-ism-teal/10 h-8" data-testid="knowledge-batch-btn">
        {running ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Researching…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {unenriched > 0 ? `Research ${Math.min(unenriched, 8)} accounts` : "All researched"}</>}
      </Button>
    </div>
  );
};
